/**
 * Conversation History Policy
 *
 * Loads the account's past conversations into `conversationHistory`.
 *
 * Nothing used to call `service.getConversations()` at all — the state slice
 * existed, the history UI rendered it, and it was permanently empty because no
 * policy ever fetched. This closes that loop.
 *
 * Refreshes on two edges rather than polling:
 *  - `init`, so the sidebar is populated the first time the panel opens.
 *  - when a turn finishes (`isProcessing` true → false), because that is when a
 *    brand-new conversation first becomes visible to the readmodel and when an
 *    existing one's `lastMessage` / `messageCount` change.
 *
 * A failure leaves the previous list in place and logs. This is a convenience
 * surface: an empty sidebar is a worse lie than a stale one, and there is no
 * user action to retry.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { AIAssistantState } from '../state';
import { AIAssistantCommand } from '../commands';
import { AIAssistantService } from '@infrastructure/aiAssistant/aiAssistantService';

export const createConversationHistoryPolicy = (
  service: AIAssistantService
): Policy<AIAssistantState, AIAssistantCommand> => {
  const load = ( dispatch: ( c: AIAssistantCommand, m: { source: string }) => void ) => {
    dispatch(
      { type: 'aiAssistant/conversation/setLoadingHistory', payload: { isLoading: true }},
      { source: 'application' }
    );
    service.getConversations()
      .then(( conversations ) => {
        dispatch(
          { type: 'aiAssistant/conversation/setHistory', payload: { conversations }},
          { source: 'api' }
        );
      })
      .catch(( error ) => {
        console.error( '[aiAssistant] Failed to load conversation history:', error );
      })
      .finally(() => {
        dispatch(
          { type: 'aiAssistant/conversation/setLoadingHistory', payload: { isLoading: false }},
          { source: 'application' }
        );
      });
  };

  return {
    name: 'ai-conversation-history',

    init: ({ dispatch }) => {
      load( dispatch as never );
    },

    // A finished turn is the only moment the readmodel can have changed.
    shouldReact: ( prev, next ) => prev.isProcessing && !next.isProcessing,

    onStateChange: ({ ctx }) => {
      load( ctx.dispatch as never );
    }
  };
};
